import { useModelLabel } from "@judo/i18n";
import type { TabController, Tab as TabElement } from "@judo/model-api";
import { getTabControllerTestId, getTabListTestId, getTabPanelTestId, getTabTestId } from "@judo/test-ids";
import { Box, Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { VisualElementRenderer } from "./VisualElementRenderer";

export interface TabControllerRendererProps {
	element: TabController;
	readOnly?: boolean;
}

/**
 * Renderer for TabController elements.
 */
export function TabControllerRenderer({ element, readOnly }: TabControllerRendererProps) {
	const [activeTab, setActiveTab] = useState(0);

	const controllerTestId = getTabControllerTestId(element);
	const controllerId = element.sourceId ?? element["xmi:id"] ?? "unknown";
	const tabListTestId = getTabListTestId(controllerId);

	return (
		<Box data-testid={controllerTestId}>
			<Tabs
				value={activeTab}
				onChange={(_, v) => setActiveTab(v)}
				orientation={element.orientation === "VERTICAL" ? "vertical" : "horizontal"}
				data-testid={tabListTestId}
			>
				{element.tabs.map((tab, index) => {
					const tabTestId = getTabTestId(controllerId, tab, index);
					return <TranslatedTab key={tab["xmi:id"] ?? index} tab={tab} testId={tabTestId} />;
				})}
			</Tabs>

			{element.tabs.map((tab, index) => {
				const panelTestId = getTabPanelTestId(controllerId, tab, index);
				return (
					<Box
						key={tab["xmi:id"] ?? index}
						role="tabpanel"
						hidden={activeTab !== index}
						p={2}
						data-testid={panelTestId}
					>
						{activeTab === index && tab.element && <VisualElementRenderer element={tab.element} readOnly={readOnly} />}
					</Box>
				);
			})}
		</Box>
	);
}

/** Tab with translated label via useModelLabel. */
function TranslatedTab({ tab, testId }: { tab: TabElement; testId: string }) {
	const { label } = useModelLabel(tab);
	return <Tab label={label} data-testid={testId} />;
}
